import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import QRActivationTool from '../components/QRActivationTool';
import EmailPreviewTool from '../components/EmailPreviewTool';
import {
    Leaf,
    SignOut,
    Users,
    Target,
    Article,
    Gear,
    Plus,
    Trash,
    PencilSimple,
    Eye,
    ChartLine,
    Key,
    Check,
    X,
    Sparkle,
    Lightning,
    Question,
    Cloud,
    Database,
    Globe,
    ShieldCheck,
    Info,
    Copy,
    ArrowSquareOut
} from '@phosphor-icons/react';

const TABS = ['overview', 'users', 'missions', 'content', 'games', 'activation', 'emails', 'settings', 'deployment'];

export default function AdminDashboard() {
    const { user, logout, api } = useAuth();
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('overview');
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState(null);
    const [users, setUsers] = useState([]);
    const [missions, setMissions] = useState([]);
    const [quizzes, setQuizzes] = useState([]);
    const [settings, setSettings] = useState({
        openai_api_key: '',
        anthropic_api_key: '',
        gemini_api_key: '',
        azure_blob_key: '',
        active_llm_provider: 'openai'
    });
    const [savingSettings, setSavingSettings] = useState(false);
    const [deploymentInfo, setDeploymentInfo] = useState(null);
    const [loadingDeployment, setLoadingDeployment] = useState(false);
    
    // Cosmos DB deployment state
    const [cosmosConnectionString, setCosmosConnectionString] = useState('');
    const [cosmosDatabaseName, setCosmosDatabaseName] = useState('bush_rangers');
    const [testingConnection, setTestingConnection] = useState(false);
    const [connectionResult, setConnectionResult] = useState(null);
    const [deployingSchema, setDeployingSchema] = useState(false);
    const [schemaResult, setSchemaResult] = useState(null);
    const [seedingData, setSeedingData] = useState(false);
    const [seedResult, setSeedResult] = useState(null);
    
    // AI Quiz Generator
    const [showQuizGenerator, setShowQuizGenerator] = useState(false);
    const [quizTopic, setQuizTopic] = useState('');
    const [generatingQuiz, setGeneratingQuiz] = useState(false);
    const [generatedQuiz, setGeneratedQuiz] = useState(null);

    // Password reset state
    const [resetResult, setResetResult] = useState(null);
    const [resettingUserId, setResettingUserId] = useState(null);

    // Games management state
    const [memoryAnimals, setMemoryAnimals] = useState([]);
    const [editingAnimal, setEditingAnimal] = useState(null);
    const [animalVideoUrl, setAnimalVideoUrl] = useState('');
    const [animalImageUrl, setAnimalImageUrl] = useState('');
    const [savingAnimal, setSavingAnimal] = useState(false);

    // Add admin state
    const [showAddAdmin, setShowAddAdmin] = useState(false);
    const [newAdminName, setNewAdminName] = useState('');
    const [newAdminEmail, setNewAdminEmail] = useState('');
    const [newAdminPassword, setNewAdminPassword] = useState('');
    const [creatingAdmin, setCreatingAdmin] = useState(false);

    // Mission form state
    const [showMissionForm, setShowMissionForm] = useState(false);
    const [missionForm, setMissionForm] = useState({
        title: '',
        description: '',
        category: 'cleanup',
        difficulty: 'easy',
        xp_reward: 100,
        proof_type: 'checkbox',
        instructions: [''],
        ecosystem: 'bush',
        image_url: ''
    });

    const loadData = async () => {
        try {
            const [statsRes, usersRes, missionsRes, settingsRes, quizzesRes] = await Promise.all([
                api().get('/admin/stats'),
                api().get('/admin/users'),
                api().get('/missions'),
                api().get('/admin/settings'),
                api().get('/quizzes')
            ]);
            setStats(statsRes.data);
            setUsers(usersRes.data);
            setMissions(missionsRes.data);
            setSettings(settingsRes.data);
            setQuizzes(quizzesRes.data);
        } catch (error) {
            console.error('Error loading admin data:', error);
            toast.error('Failed to load admin data');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadDeploymentInfo = async () => {
        setLoadingDeployment(true);
        try {
            const response = await api().get('/deployment-info');
            setDeploymentInfo(response.data);
        } catch (error) {
            toast.error('Failed to load deployment info');
        } finally {
            setLoadingDeployment(false);
        }
    };

    useEffect(() => {
        if (activeTab === 'deployment' && !deploymentInfo) {
            loadDeploymentInfo();
        }
        if (activeTab === 'games' && memoryAnimals.length === 0) {
            loadMemoryAnimals();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab]);

    const loadMemoryAnimals = async () => {
        try {
            const res = await api().get('/admin/games/memory-animals');
            setMemoryAnimals(res.data);
        } catch (err) { console.error('Failed to load memory animals'); }
    };

    const handleEditAnimal = (animal) => {
        setEditingAnimal(animal);
        setAnimalVideoUrl(animal.video || '');
        setAnimalImageUrl(animal.image_url || '');
    };

    const handleSaveAnimal = async () => {
        if (!editingAnimal) return;
        setSavingAnimal(true);
        try {
            const res = await api().put(`/admin/games/memory-animals/${editingAnimal.id}`, {
                video: animalVideoUrl,
                image_url: animalImageUrl
            });
            setMemoryAnimals(prev => prev.map(a => a.id === editingAnimal.id ? res.data : a));
            setEditingAnimal(null);
            toast.success(`${editingAnimal.name} updated`);
        } catch (err) {
            toast.error('Failed to update animal');
        } finally {
            setSavingAnimal(false);
        }
    };

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
        toast.success('Copied to clipboard!');
    };

    // Cosmos DB deployment functions
    const testCosmosConnection = async () => {
        if (!cosmosConnectionString.trim()) {
            toast.error('Please enter a connection string');
            return;
        }
        
        setTestingConnection(true);
        setConnectionResult(null);
        
        try {
            const response = await api().post('/deployment/test-connection', {
                connection_string: cosmosConnectionString,
                database_name: cosmosDatabaseName
            });
            setConnectionResult(response.data);
            
            if (response.data.success) {
                toast.success('Connection successful!');
            } else {
                toast.error('Connection failed');
            }
        } catch (error) {
            setConnectionResult({ success: false, message: error.response?.data?.detail || 'Connection test failed' });
            toast.error('Connection test failed');
        } finally {
            setTestingConnection(false);
        }
    };

    const deploySchema = async () => {
        if (!connectionResult?.success) {
            toast.error('Please test the connection first');
            return;
        }
        
        setDeployingSchema(true);
        setSchemaResult(null);
        
        try {
            const response = await api().post('/deployment/deploy-schema', {
                connection_string: cosmosConnectionString,
                database_name: cosmosDatabaseName
            });
            setSchemaResult(response.data);
            toast.success('Schema deployed successfully!');
        } catch (error) {
            setSchemaResult({ success: false, message: error.response?.data?.detail || 'Schema deployment failed' });
            toast.error('Schema deployment failed');
        } finally {
            setDeployingSchema(false);
        }
    };

    const seedRemoteData = async () => {
        if (!schemaResult?.success) {
            toast.error('Please deploy schema first');
            return;
        }
        
        setSeedingData(true);
        setSeedResult(null);
        
        try {
            const response = await api().post('/deployment/seed-data', {
                connection_string: cosmosConnectionString,
                database_name: cosmosDatabaseName
            });
            setSeedResult(response.data);
            toast.success('Data seeded successfully!');
        } catch (error) {
            setSeedResult({ success: false, message: error.response?.data?.detail || 'Data seeding failed' });
            toast.error('Data seeding failed');
        } finally {
            setSeedingData(false);
        }
    };

    const handleLogout = () => {
        logout();
        navigate('/');
        toast.success('Logged out successfully');
    };

    const handleDeleteUser = async (userId) => {
        if (!window.confirm('Are you sure you want to delete this user?')) return;
        
        try {
            await api().delete(`/admin/users/${userId}`);
            setUsers(users.filter(u => u.id !== userId));
            toast.success('User deleted');
        } catch (error) {
            toast.error('Failed to delete user');
        }
    };

    const handleResetPassword = async (userId, userName) => {
        if (!window.confirm(`Reset password for ${userName}? They will need to set a new password on next login.`)) return;
        
        setResettingUserId(userId);
        try {
            const res = await api().post(`/admin/users/${userId}/reset-password`);
            setResetResult({ userName, tempPassword: res.data.temporary_password });
            toast.success(`Password reset for ${userName}`);
        } catch (error) {
            toast.error('Failed to reset password');
        } finally {
            setResettingUserId(null);
        }
    };

    const handleCreateAdmin = async (e) => {
        e.preventDefault();
        if (!newAdminName || !newAdminEmail || !newAdminPassword) {
            toast.error('Please fill in all fields'); return;
        }
        if (newAdminPassword.length < 6) {
            toast.error('Password must be at least 6 characters'); return;
        }
        setCreatingAdmin(true);
        try {
            await api().post('/admin/create-admin', {
                name: newAdminName, email: newAdminEmail, password: newAdminPassword
            });
            toast.success(`${newAdminName} added as admin`);
            setShowAddAdmin(false);
            setNewAdminName(''); setNewAdminEmail(''); setNewAdminPassword('');
            // Refresh users list
            const res = await api().get('/admin/users');
            setUsers(res.data);
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to create admin');
        } finally {
            setCreatingAdmin(false);
        }
    };

    const handleSaveSettings = async () => {
        setSavingSettings(true);
        try {
            await api().put('/admin/settings', settings);
            toast.success('Settings saved successfully');
        } catch (error) {
            toast.error('Failed to save settings');
        } finally {
            setSavingSettings(false);
        }
    };

    const handleCreateMission = async (e) => {
        e.preventDefault();
        try {
            const response = await api().post('/missions', {
                ...missionForm,
                instructions: missionForm.instructions.filter(i => i.trim())
            });
            setMissions([...missions, response.data]);
            setShowMissionForm(false);
            setMissionForm({
                title: '',
                description: '',
                category: 'cleanup',
                difficulty: 'easy',
                xp_reward: 100,
                proof_type: 'checkbox',
                instructions: [''],
                ecosystem: 'bush',
                image_url: ''
            });
            toast.success('Mission created!');
        } catch (error) {
            toast.error('Failed to create mission');
        }
    };

    const handleDeleteMission = async (missionId) => {
        if (!window.confirm('Delete this mission?')) return;
        
        try {
            await api().delete(`/admin/missions/${missionId}`);
            setMissions(missions.filter(m => m.id !== missionId));
            toast.success('Mission deleted');
        } catch (error) {
            toast.error('Failed to delete mission');
        }
    };

    const seedData = async () => {
        try {
            const response = await api().post('/seed');
            toast.success(response.data.message);
            loadData();
        } catch (error) {
            toast.error('Failed to seed data');
        }
    };

    const handleGenerateQuiz = async () => {
        if (!quizTopic.trim()) {
            toast.error('Please enter a topic');
            return;
        }
        
        setGeneratingQuiz(true);
        try {
            const response = await api().post(`/ai/generate-quiz?topic=${encodeURIComponent(quizTopic)}`);
            setGeneratedQuiz(response.data);
            toast.success('Quiz generated! Review and save it.');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to generate quiz');
        } finally {
            setGeneratingQuiz(false);
        }
    };

    const handleSaveGeneratedQuiz = async () => {
        if (!generatedQuiz) return;
        
        try {
            const response = await api().post('/quizzes', {
                title: generatedQuiz.title,
                category: 'ai-generated',
                questions: generatedQuiz.questions,
                xp_reward: 50
            });
            setQuizzes([...quizzes, response.data]);
            setGeneratedQuiz(null);
            setQuizTopic('');
            setShowQuizGenerator(false);
            toast.success('Quiz saved successfully!');
        } catch (error) {
            toast.error('Failed to save quiz');
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-[hsl(53,92%,96%)] flex items-center justify-center">
                <div className="brutalist-card p-8 text-center">
                    <div className="w-16 h-16 border-4 border-[#22c55e] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                    <p className="font-fredoka text-lg">Loading admin panel...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[hsl(53,92%,96%)]">
            {/* Navigation */}
            <nav className="fixed top-0 left-0 right-0 z-50 bg-[#1e293b] border-b-4 border-[#1e293b]">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-center h-16">
                        <div className="flex items-center gap-4">
                            <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
                                <div className="w-10 h-10 rounded-full bg-[#22c55e] flex items-center justify-center">
                                    <Leaf weight="fill" className="text-white" size={20} />
                                </div>
                                <span className="font-fredoka text-xl font-bold text-white hidden sm:block">
                                    Admin Panel
                                </span>
                            </Link>
                        </div>
                        
                        <div className="flex items-center gap-4">
                            <span className="text-white/80 font-semibold hidden sm:block">
                                {user?.name}
                            </span>
                            <Link
                                to="/account"
                                className="relative p-2 rounded-full hover:bg-white/10"
                                data-testid="account-settings-link"
                                title="Account Settings"
                            >
                                <Gear weight="bold" size={22} className="text-white" />
                                {user?.email_verified === false && (
                                    <span className="absolute top-1 right-1 w-2.5 h-2.5 rounded-full bg-[#ef4444] border-2 border-[#0f172a]" data-testid="account-unverified-dot" />
                                )}
                            </Link>
                            <motion.button
                                onClick={handleLogout}
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                className="p-2 rounded-full hover:bg-white/10"
                                data-testid="logout-btn"
                            >
                                <SignOut weight="bold" size={24} className="text-white" />
                            </motion.button>
                        </div>
                    </div>
                </div>
            </nav>

            {/* Tabs */}
            <div className="bg-white border-b-4 border-[#1e293b] sticky top-16 z-40">
                <div className="max-w-7xl mx-auto px-4 overflow-x-auto">
                    <div className="flex gap-2 py-3">
                        {TABS.map((tab) => (
                            <button
                                key={tab}
                                onClick={() => setActiveTab(tab)}
                                className={`px-4 py-2 rounded-full font-fredoka font-semibold text-sm capitalize transition-all whitespace-nowrap ${
                                    activeTab === tab
                                        ? 'bg-[#22c55e] text-white border-4 border-[#1e293b] shadow-[2px_2px_0_0_#1e293b]'
                                        : 'bg-gray-100 text-[#1e293b] hover:bg-gray-200'
                                }`}
                                data-testid={`tab-${tab}`}
                            >
                                {tab}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {/* Main Content */}
            <main className="pt-6 px-4 pb-8 max-w-7xl mx-auto mt-16">
                {/* Overview Tab */}
                {activeTab === 'overview' && stats && (
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                    >
                        <div className="flex justify-between items-center mb-6">
                            <h1 className="font-fredoka text-2xl font-bold text-[#1e293b]">
                                Dashboard Overview
                            </h1>
                            <motion.button
                                onClick={seedData}
                                whileHover={{ y: -2, x: -2 }}
                                whileTap={{ y: 2, x: 2 }}
                                className="brutalist-btn bg-[#fde047] text-[#1e293b] text-sm"
                                data-testid="seed-data-btn"
                            >
                                Seed Sample Data
                            </motion.button>
                        </div>

                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                            <div className="brutalist-card p-4" style={{ background: '#22c55e' }}>
                                <Users weight="duotone" className="text-white mb-2" size={32} />
                                <p className="font-fredoka text-3xl font-bold text-white">{stats.total_users}</p>
                                <p className="text-white/80 text-sm">Total Users</p>
                            </div>
                            <div className="brutalist-card p-4" style={{ background: '#38bdf8' }}>
                                <Target weight="duotone" className="text-white mb-2" size={32} />
                                <p className="font-fredoka text-3xl font-bold text-white">{stats.total_missions}</p>
                                <p className="text-white/80 text-sm">Missions</p>
                            </div>
                            <div className="brutalist-card p-4" style={{ background: '#fde047' }}>
                                <ChartLine weight="duotone" className="text-[#1e293b] mb-2" size={32} />
                                <p className="font-fredoka text-3xl font-bold text-[#1e293b]">{stats.total_completions}</p>
                                <p className="text-[#1e293b]/80 text-sm">Completions</p>
                            </div>
                            <div className="brutalist-card p-4" style={{ background: '#fb7185' }}>
                                <Article weight="duotone" className="text-white mb-2" size={32} />
                                <p className="font-fredoka text-3xl font-bold text-white">{stats.total_articles}</p>
                                <p className="text-white/80 text-sm">Articles</p>
                            </div>
                        </div>

                        <div className="grid md:grid-cols-2 gap-6">
                            <div className="brutalist-card p-6">
                                <h3 className="font-fredoka text-lg font-bold text-[#1e293b] mb-4">User Breakdown</h3>
                                <div className="space-y-3">
                                    <div className="flex justify-between items-center">
                                        <span className="text-[#475569]">Parents</span>
                                        <span className="font-fredoka font-bold">{stats.total_parents}</span>
                                    </div>
                                    <div className="flex justify-between items-center">
                                        <span className="text-[#475569]">Children</span>
                                        <span className="font-fredoka font-bold">{stats.total_children}</span>
                                    </div>
                                    <div className="flex justify-between items-center">
                                        <span className="text-[#475569]">Animals in Library</span>
                                        <span className="font-fredoka font-bold">{stats.total_animals}</span>
                                    </div>
                                </div>
                            </div>
                            
                            <div className="brutalist-card p-6">
                                <h3 className="font-fredoka text-lg font-bold text-[#1e293b] mb-4">Quick Actions</h3>
                                <div className="space-y-3">
                                    <button 
                                        onClick={() => setActiveTab('missions')}
                                        className="w-full text-left p-3 rounded-xl hover:bg-[#22c55e]/10 flex items-center gap-3"
                                    >
                                        <Plus weight="bold" className="text-[#22c55e]" size={20} />
                                        <span className="font-semibold">Create New Mission</span>
                                    </button>
                                    <button 
                                        onClick={() => setActiveTab('settings')}
                                        className="w-full text-left p-3 rounded-xl hover:bg-[#38bdf8]/10 flex items-center gap-3"
                                    >
                                        <Key weight="bold" className="text-[#38bdf8]" size={20} />
                                        <span className="font-semibold">Configure API Keys</span>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </motion.div>
                )}

                {/* Users Tab */}
                {activeTab === 'users' && (
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                    >
                        <div className="flex justify-between items-center mb-6">
                            <h1 className="font-fredoka text-2xl font-bold text-[#1e293b]">
                                User Management
                            </h1>
                            <motion.button
                                onClick={() => setShowAddAdmin(true)}
                                whileHover={{ y: -2, x: -2 }}
                                whileTap={{ y: 2, x: 2 }}
                                className="brutalist-btn bg-[#fb7185] text-white flex items-center gap-2 text-sm"
                                data-testid="add-admin-btn"
                            >
                                <Plus weight="bold" size={18} /> Add Admin
                            </motion.button>
                        </div>
                        
                        <div className="brutalist-card overflow-hidden">
                            <div className="overflow-x-auto">
                                <table className="w-full">
                                    <thead className="bg-[#1e293b] text-white">
                                        <tr>
                                            <th className="px-4 py-3 text-left font-fredoka">Name</th>
                                            <th className="px-4 py-3 text-left font-fredoka">Email</th>
                                            <th className="px-4 py-3 text-left font-fredoka">Role</th>
                                            <th className="px-4 py-3 text-left font-fredoka">Tokens</th>
                                            <th className="px-4 py-3 text-left font-fredoka">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {users.map((u, index) => (
                                            <tr key={u.id} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                                <td className="px-4 py-3 font-semibold">{u.name}</td>
                                                <td className="px-4 py-3 text-[#475569]">{u.email || '-'}</td>
                                                <td className="px-4 py-3">
                                                    <span className={`px-2 py-1 rounded-full text-xs font-bold ${
                                                        u.role === 'admin' ? 'bg-[#fb7185] text-white' :
                                                        u.role === 'parent' ? 'bg-[#38bdf8] text-white' :
                                                        'bg-[#22c55e] text-white'
                                                    }`}>
                                                        {u.role}
                                                    </span>
                                                </td>
                                                <td className="px-4 py-3 font-fredoka">{u.xp || 0}</td>
                                                <td className="px-4 py-3">
                                                    {u.role !== 'admin' && (
                                                        <div className="flex items-center gap-1">
                                                            <button
                                                                onClick={() => handleResetPassword(u.id, u.name)}
                                                                disabled={resettingUserId === u.id}
                                                                className="p-2 rounded-lg hover:bg-amber-100 text-amber-600 disabled:opacity-50"
                                                                title="Reset Password"
                                                                data-testid={`reset-password-${u.id}`}
                                                            >
                                                                {resettingUserId === u.id ? (
                                                                    <div className="w-[18px] h-[18px] border-2 border-amber-600 border-t-transparent rounded-full animate-spin" />
                                                                ) : (
                                                                    <Key weight="bold" size={18} />
                                                                )}
                                                            </button>
                                                            <button
                                                                onClick={() => handleDeleteUser(u.id)}
                                                                className="p-2 rounded-lg hover:bg-red-100 text-red-500"
                                                                data-testid={`delete-user-${u.id}`}
                                                            >
                                                                <Trash weight="bold" size={18} />
                                                            </button>
                                                        </div>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        {/* Temporary Password Modal */}
                        {resetResult && (
                            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                                <motion.div
                                    initial={{ opacity: 0, scale: 0.9 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    className="brutalist-card p-6 max-w-md w-full"
                                >
                                    <h3 className="font-fredoka text-xl font-bold text-[#1e293b] mb-2">
                                        Password Reset
                                    </h3>
                                    <p className="text-[#475569] mb-4">
                                        Temporary password for <strong>{resetResult.userName}</strong>:
                                    </p>
                                    <div className="bg-[#f1f5f9] border-2 border-[#1e293b] rounded-xl p-4 font-mono text-lg text-center select-all mb-4" data-testid="temp-password-display">
                                        {resetResult.tempPassword}
                                    </div>
                                    <p className="text-sm text-[#64748b] mb-4">
                                        The user will be asked to set a new password on their next login.
                                    </p>
                                    <div className="flex gap-3">
                                        <button
                                            onClick={() => {
                                                navigator.clipboard.writeText(resetResult.tempPassword);
                                                toast.success('Copied to clipboard');
                                            }}
                                            className="brutalist-btn bg-[#38bdf8] text-white flex-1 flex items-center justify-center gap-2"
                                            data-testid="copy-temp-password"
                                        >
                                            <Copy weight="bold" size={18} />
                                            Copy
                                        </button>
                                        <button
                                            onClick={() => setResetResult(null)}
                                            className="brutalist-btn bg-[#1e293b] text-white flex-1"
                                            data-testid="close-reset-modal"
                                        >
                                            Done
                                        </button>
                                    </div>
                                </motion.div>
                            </div>
                        )}

                        {/* Add Admin Modal */}
                        {showAddAdmin && (
                            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                                <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}
                                    className="brutalist-card p-6 max-w-md w-full bg-white">
                                    <h3 className="font-fredoka text-xl font-bold text-[#1e293b] mb-2">Add Admin User</h3>
                                    <p className="text-sm text-[#475569] mb-4">Create a new admin account. They'll be asked to set a new password on first login.</p>

                                    <form onSubmit={handleCreateAdmin} className="space-y-4">
                                        <div>
                                            <label className="block font-fredoka font-semibold text-[#1e293b] mb-2">Name</label>
                                            <input type="text" value={newAdminName} onChange={(e) => setNewAdminName(e.target.value)}
                                                className="brutalist-input w-full" placeholder="Admin's name" data-testid="admin-name-input" />
                                        </div>
                                        <div>
                                            <label className="block font-fredoka font-semibold text-[#1e293b] mb-2">Email</label>
                                            <input type="email" value={newAdminEmail} onChange={(e) => setNewAdminEmail(e.target.value)}
                                                className="brutalist-input w-full" placeholder="admin@email.com" data-testid="admin-email-input" />
                                        </div>
                                        <div>
                                            <label className="block font-fredoka font-semibold text-[#1e293b] mb-2">Temporary Password</label>
                                            <input type="text" value={newAdminPassword} onChange={(e) => setNewAdminPassword(e.target.value)}
                                                className="brutalist-input w-full" placeholder="At least 6 characters" data-testid="admin-password-input" />
                                        </div>
                                        <div className="flex gap-3">
                                            <button type="button" onClick={() => setShowAddAdmin(false)}
                                                className="brutalist-btn flex-1 bg-white text-[#1e293b]" data-testid="cancel-add-admin">Cancel</button>
                                            <button type="submit" disabled={creatingAdmin}
                                                className="brutalist-btn flex-1 bg-[#fb7185] text-white disabled:opacity-50" data-testid="submit-add-admin">
                                                {creatingAdmin ? 'Creating...' : 'Create Admin'}
                                            </button>
                                        </div>
                                    </form>
                                </motion.div>
                            </div>
                        )}
                    </motion.div>
                )}

                {/* Missions Tab */}
                {activeTab === 'missions' && (
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                    >
                        <div className="flex justify-between items-center mb-6">
                            <h1 className="font-fredoka text-2xl font-bold text-[#1e293b]">
                                Mission Management
                            </h1>
                            <motion.button
                                onClick={() => setShowMissionForm(true)}
                                whileHover={{ y: -2, x: -2 }}
                                whileTap={{ y: 2, x: 2 }}
                                className="brutalist-btn bg-[#22c55e] text-white flex items-center gap-2"
                                data-testid="add-mission-btn"
                            >
                                <Plus weight="bold" size={20} />
                                Add Mission
                            </motion.button>
                        </div>

                        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {missions.map((mission) => (
                                <div key={mission.id} className="brutalist-card p-4">
                                    <div className="flex justify-between items-start mb-2">
                                        <h3 className="font-fredoka font-bold text-[#1e293b]">{mission.title}</h3>
                                        <button
                                            onClick={() => handleDeleteMission(mission.id)}
                                            className="p-1 rounded hover:bg-red-100 text-red-500"
                                            data-testid={`delete-mission-${mission.id}`}
                                        >
                                            <Trash weight="bold" size={16} />
                                        </button>
                                    </div>
                                    <p className="text-sm text-[#475569] line-clamp-2 mb-3">{mission.description}</p>
                                    <div className="flex flex-wrap gap-2">
                                        <span className="px-2 py-1 bg-[#22c55e]/10 rounded-lg text-xs font-semibold text-[#22c55e]">
                                            {mission.category}
                                        </span>
                                        <span className="px-2 py-1 bg-[#fde047]/20 rounded-lg text-xs font-semibold text-[#1e293b]">
                                            +{mission.xp_reward} Tokens
                                        </span>
                                        <span className={`px-2 py-1 rounded-lg text-xs font-semibold ${
                                            mission.difficulty === 'easy' ? 'bg-green-100 text-green-700' :
                                            mission.difficulty === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                                            'bg-red-100 text-red-700'
                                        }`}>
                                            {mission.difficulty}
                                        </span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </motion.div>
                )}

                {/* Content Tab */}
                {activeTab === 'content' && (
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                    >
                        <div className="flex justify-between items-center mb-6">
                            <h1 className="font-fredoka text-2xl font-bold text-[#1e293b]">
                                Content Management
                            </h1>
                            <motion.button
                                onClick={() => setShowQuizGenerator(true)}
                                whileHover={{ y: -2, x: -2 }}
                                whileTap={{ y: 2, x: 2 }}
                                className="brutalist-btn bg-gradient-to-r from-[#8b5cf6] to-[#6366f1] text-white flex items-center gap-2"
                                data-testid="generate-quiz-btn"
                            >
                                <Sparkle weight="fill" size={20} />
                                AI Quiz Generator
                            </motion.button>
                        </div>

                        {/* Existing Quizzes */}
                        <h2 className="font-fredoka text-xl font-bold text-[#1e293b] mb-4">
                            Quizzes ({quizzes.length})
                        </h2>
                        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
                            {quizzes.map((quiz) => (
                                <div key={quiz.id} className="brutalist-card p-4 bg-gradient-to-br from-[#fde047]/30 to-[#fde047]/10">
                                    <h3 className="font-fredoka font-bold text-[#1e293b] mb-2">{quiz.title}</h3>
                                    <p className="text-sm text-[#475569] mb-3">
                                        {quiz.questions?.length || 0} questions • +{quiz.xp_reward} Tokens
                                    </p>
                                    <span className="px-2 py-1 bg-[#fde047]/50 rounded-lg text-xs font-semibold text-[#1e293b] capitalize">
                                        {quiz.category}
                                    </span>
                                </div>
                            ))}
                        </div>

                        {quizzes.length === 0 && (
                            <div className="brutalist-card p-8 text-center mb-8">
                                <Article weight="duotone" className="text-[#475569] mx-auto mb-4" size={48} />
                                <p className="text-[#475569]">
                                    No quizzes yet. Use the AI Quiz Generator to create some!
                                </p>
                            </div>
                        )}
                    </motion.div>
                )}

                {/* Games Tab */}
                {activeTab === 'games' && (
                    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
                        <h1 className="font-fredoka text-2xl font-bold text-[#1e293b] mb-6">Games Management</h1>

                        {/* Memory Animals Section */}
                        <div className="brutalist-card p-6 mb-6">
                            <h2 className="font-fredoka text-xl font-bold text-[#1e293b] mb-1">Memory Match Animals</h2>
                            <p className="text-sm text-[#475569] mb-4">Manage images and YouTube videos for the Memory Match game. Paste YouTube embed URLs (format: https://www.youtube.com/embed/VIDEO_ID).</p>

                            <div className="space-y-3">
                                {memoryAnimals.map(animal => (
                                    <div key={animal.id} className="flex items-center gap-4 p-3 bg-[#f8fafc] rounded-xl border-2 border-[#e2e8f0]" data-testid={`animal-row-${animal.id}`}>
                                        {animal.image_url ? (
                                            <img src={animal.image_url} alt={animal.name} className="w-14 h-14 rounded-xl object-cover border-2 border-[#1e293b] flex-shrink-0" />
                                        ) : (
                                            <span className="text-3xl w-14 text-center">{animal.emoji}</span>
                                        )}
                                        <div className="flex-1 min-w-0">
                                            <p className="font-fredoka font-bold text-[#1e293b]">{animal.name}</p>
                                            <p className="text-xs text-[#475569] truncate">{animal.fact}</p>
                                            <div className="flex items-center gap-2 mt-1">
                                                {animal.video && animal.video.length > 5 ? (
                                                    <span className="text-xs px-2 py-0.5 bg-[#22c55e]/10 text-[#22c55e] rounded-full font-semibold">Video set</span>
                                                ) : (
                                                    <span className="text-xs px-2 py-0.5 bg-[#f59e0b]/10 text-[#f59e0b] rounded-full font-semibold">No video</span>
                                                )}
                                            </div>
                                        </div>
                                        <button
                                            onClick={() => handleEditAnimal(animal)}
                                            className="brutalist-btn bg-white text-[#1e293b] text-sm px-3 py-1.5"
                                            data-testid={`edit-animal-${animal.id}`}
                                        >
                                            Edit
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Edit Animal Modal */}
                        {editingAnimal && (
                            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                                <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}
                                    className="brutalist-card p-6 max-w-lg w-full bg-white">
                                    <div className="flex items-center gap-3 mb-4">
                                        {editingAnimal.image_url ? (
                                            <img src={editingAnimal.image_url} alt={editingAnimal.name} className="w-16 h-16 rounded-xl object-cover border-2 border-[#1e293b]" />
                                        ) : (
                                            <span className="text-4xl">{editingAnimal.emoji}</span>
                                        )}
                                        <div>
                                            <h3 className="font-fredoka text-xl font-bold text-[#1e293b]">Edit {editingAnimal.name}</h3>
                                            <p className="text-xs text-[#475569]">{editingAnimal.fact}</p>
                                        </div>
                                    </div>

                                    <div className="space-y-4">
                                        <div>
                                            <label className="block font-fredoka font-semibold text-[#1e293b] mb-2">Image URL</label>
                                            <input type="text" value={animalImageUrl} onChange={(e) => setAnimalImageUrl(e.target.value)}
                                                className="brutalist-input w-full text-sm" placeholder="https://images.unsplash.com/..."
                                                data-testid="animal-image-input" />
                                            {animalImageUrl && (
                                                <img src={animalImageUrl} alt="Preview" className="mt-2 w-full h-32 object-cover rounded-xl border-2 border-[#e2e8f0]"
                                                    onError={(e) => { e.target.style.display = 'none'; }} />
                                            )}
                                        </div>
                                        <div>
                                            <label className="block font-fredoka font-semibold text-[#1e293b] mb-2">YouTube Embed URL</label>
                                            <input type="text" value={animalVideoUrl} onChange={(e) => setAnimalVideoUrl(e.target.value)}
                                                className="brutalist-input w-full text-sm" placeholder="https://www.youtube.com/embed/VIDEO_ID"
                                                data-testid="animal-video-input" />
                                            <p className="text-xs text-[#94a3b8] mt-1">Leave empty for no video. Use embed URL format.</p>
                                            {animalVideoUrl && animalVideoUrl.includes('youtube.com/embed') && (
                                                <div className="mt-2 aspect-video rounded-xl overflow-hidden border-2 border-[#e2e8f0]">
                                                    <iframe src={animalVideoUrl} title="Preview" className="w-full h-full" allowFullScreen />
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    <div className="flex gap-3 mt-6">
                                        <button onClick={() => setEditingAnimal(null)}
                                            className="brutalist-btn flex-1 bg-white text-[#1e293b]" data-testid="cancel-edit-animal">Cancel</button>
                                        <button onClick={handleSaveAnimal} disabled={savingAnimal}
                                            className="brutalist-btn flex-1 bg-[#22c55e] text-white disabled:opacity-50" data-testid="save-animal">
                                            {savingAnimal ? 'Saving...' : 'Save'}
                                        </button>
                                    </div>
                                </motion.div>
                            </div>
                        )}
                    </motion.div>
                )}

                {/* Settings Tab */}
                {activeTab === 'settings' && (
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                    >
                        <h1 className="font-fredoka text-2xl font-bold text-[#1e293b] mb-6">
                            System Settings
                        </h1>

                        <div className="brutalist-card p-6 mb-6">
                            <h2 className="font-fredoka text-xl font-bold text-[#1e293b] mb-4 flex items-center gap-2">
                                <Key weight="duotone" className="text-[#22c55e]" size={24} />
                                API Key Configuration
                            </h2>
                            <p className="text-[#475569] mb-6">
                                Configure your API keys for AI-powered content generation.
                            </p>

                            <div className="space-y-4">
                                <div>
                                    <label className="block font-fredoka font-semibold text-[#1e293b] mb-2">
                                        Active LLM Provider
                                    </label>
                                    <select
                                        value={settings.active_llm_provider}
                                        onChange={(e) => setSettings({ ...settings, active_llm_provider: e.target.value })}
                                        className="brutalist-input w-full max-w-md"
                                        data-testid="llm-provider-select"
                                    >
                                        <option value="openai">OpenAI</option>
                                        <option value="anthropic">Anthropic (Claude)</option>
                                        <option value="gemini">Google (Gemini)</option>
                                    </select>
                                </div>

                                <div>
                                    <label className="block font-fredoka font-semibold text-[#1e293b] mb-2">
                                        OpenAI API Key
                                    </label>
                                    <input
                                        type="password"
                                        value={settings.openai_api_key}
                                        onChange={(e) => setSettings({ ...settings, openai_api_key: e.target.value })}
                                        className="brutalist-input w-full max-w-md"
                                        placeholder="sk-..."
                                        data-testid="openai-key-input"
                                    />
                                </div>

                                <div>
                                    <label className="block font-fredoka font-semibold text-[#1e293b] mb-2">
                                        Anthropic API Key
                                    </label>
                                    <input
                                        type="password"
                                        value={settings.anthropic_api_key}
                                        onChange={(e) => setSettings({ ...settings, anthropic_api_key: e.target.value })}
                                        className="brutalist-input w-full max-w-md"
                                        placeholder="sk-ant-..."
                                        data-testid="anthropic-key-input"
                                    />
                                </div>

                                <div>
                                    <label className="block font-fredoka font-semibold text-[#1e293b] mb-2">
                                        Google Gemini API Key
                                    </label>
                                    <input
                                        type="password"
                                        value={settings.gemini_api_key}
                                        onChange={(e) => setSettings({ ...settings, gemini_api_key: e.target.value })}
                                        className="brutalist-input w-full max-w-md"
                                        placeholder="AIza..."
                                        data-testid="gemini-key-input"
                                    />
                                </div>

                                <div>
                                    <label className="block font-fredoka font-semibold text-[#1e293b] mb-2">
                                        Azure Blob Storage Key
                                    </label>
                                    <input
                                        type="password"
                                        value={settings.azure_blob_key}
                                        onChange={(e) => setSettings({ ...settings, azure_blob_key: e.target.value })}
                                        className="brutalist-input w-full max-w-md"
                                        placeholder="Azure storage connection string"
                                        data-testid="azure-blob-key-input"
                                    />
                                    <p className="text-sm text-[#475569] mt-1">
                                        For photo upload feature (coming soon)
                                    </p>
                                </div>

                                <motion.button
                                    onClick={handleSaveSettings}
                                    disabled={savingSettings}
                                    whileHover={{ y: -2, x: -2 }}
                                    whileTap={{ y: 2, x: 2 }}
                                    className="brutalist-btn bg-[#22c55e] text-white mt-4 disabled:opacity-50"
                                    data-testid="save-settings-btn"
                                >
                                    {savingSettings ? 'Saving...' : 'Save Settings'}
                                </motion.button>
                            </div>
                        </div>
                    </motion.div>
                )}

                {/* Deployment Tab */}
                {activeTab === 'deployment' && (
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                    >
                        <div className="flex justify-between items-center mb-6">
                            <h1 className="font-fredoka text-2xl font-bold text-[#1e293b] flex items-center gap-2">
                                <Cloud weight="duotone" className="text-[#38bdf8]" size={32} />
                                Azure Deployment
                            </h1>
                            <motion.button
                                onClick={loadDeploymentInfo}
                                whileHover={{ y: -2, x: -2 }}
                                whileTap={{ y: 2, x: 2 }}
                                className="brutalist-btn bg-[#38bdf8] text-white text-sm"
                                disabled={loadingDeployment}
                            >
                                {loadingDeployment ? 'Loading...' : 'Refresh Status'}
                            </motion.button>
                        </div>

                        {/* Current Environment Status */}
                        <div className="brutalist-card p-6 mb-6">
                            <h2 className="font-fredoka text-xl font-bold text-[#1e293b] mb-4 flex items-center gap-2">
                                <Database weight="duotone" className="text-[#22c55e]" size={24} />
                                Current Environment
                            </h2>
                            
                            {loadingDeployment ? (
                                <div className="flex items-center gap-3 text-[#475569]">
                                    <div className="w-5 h-5 border-2 border-[#22c55e] border-t-transparent rounded-full animate-spin"></div>
                                    Loading configuration...
                                </div>
                            ) : deploymentInfo ? (
                                <div className="space-y-3">
                                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                                        <span className="text-[#475569]">MongoDB/Cosmos DB</span>
                                        <span className="font-mono text-sm bg-green-100 text-green-700 px-2 py-1 rounded">
                                            {deploymentInfo.environment.mongo_url}
                                        </span>
                                    </div>
                                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                                        <span className="text-[#475569]">Database Name</span>
                                        <span className="font-mono text-sm">{deploymentInfo.environment.db_name}</span>
                                    </div>
                                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                                        <span className="text-[#475569]">JWT Secret</span>
                                        <span className={`font-mono text-sm px-2 py-1 rounded ${
                                            deploymentInfo.environment.jwt_secret.includes('fallback') 
                                                ? 'bg-red-100 text-red-700' 
                                                : 'bg-green-100 text-green-700'
                                        }`}>
                                            {deploymentInfo.environment.jwt_secret}
                                        </span>
                                    </div>
                                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                                        <span className="text-[#475569]">Emergent LLM Key</span>
                                        <span className="font-mono text-sm bg-green-100 text-green-700 px-2 py-1 rounded">
                                            {deploymentInfo.environment.emergent_llm_key}
                                        </span>
                                    </div>
                                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                                        <span className="text-[#475569]">CORS Origins</span>
                                        <span className="font-mono text-sm">{deploymentInfo.environment.cors_origins}</span>
                                    </div>
                                </div>
                            ) : (
                                <p className="text-[#475569]">Click "Refresh Status" to load configuration</p>
                            )}
                        </div>

                        {/* Cosmos DB Configuration */}
                        <div className="brutalist-card p-6 mb-6" style={{ background: '#f0fdf4' }}>
                            <h2 className="font-fredoka text-xl font-bold text-[#1e293b] mb-4 flex items-center gap-2">
                                <Database weight="duotone" className="text-[#22c55e]" size={24} />
                                Deploy to Azure Cosmos DB
                            </h2>
                            
                            <p className="text-[#475569] mb-4">
                                Connect to your Azure Cosmos DB (MongoDB API) and deploy the required schema and initial data.
                            </p>

                            {/* Step 1: Connection String */}
                            <div className="mb-6">
                                <label className="block font-fredoka font-semibold text-[#1e293b] mb-2">
                                    Step 1: Connection String
                                </label>
                                <textarea
                                    value={cosmosConnectionString}
                                    onChange={(e) => setCosmosConnectionString(e.target.value)}
                                    placeholder="mongodb://username:password@hostname.mongo.cosmos.azure.com:10255/?ssl=true&replicaSet=globaldb&retryWrites=false"
                                    className="brutalist-input w-full h-24 font-mono text-sm"
                                    data-testid="cosmos-connection-string"
                                />
                                <p className="text-xs text-[#475569] mt-1">
                                    Get this from Azure Portal → Cosmos DB → Connection Strings → Primary Connection String
                                </p>
                            </div>

                            {/* Database Name */}
                            <div className="mb-6">
                                <label className="block font-fredoka font-semibold text-[#1e293b] mb-2">
                                    Database Name
                                </label>
                                <input
                                    type="text"
                                    value={cosmosDatabaseName}
                                    onChange={(e) => setCosmosDatabaseName(e.target.value)}
                                    className="brutalist-input w-full font-mono"
                                    data-testid="cosmos-db-name"
                                />
                            </div>

                            {/* Test Connection Button */}
                            <motion.button
                                onClick={testCosmosConnection}
                                disabled={testingConnection || !cosmosConnectionString.trim()}
                                whileHover={{ y: -2, x: -2 }}
                                whileTap={{ y: 2, x: 2 }}
                                className="brutalist-btn bg-[#38bdf8] text-white w-full mb-4 disabled:opacity-50"
                                data-testid="test-connection-btn"
                            >
                                {testingConnection ? (
                                    <span className="flex items-center justify-center gap-2">
                                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                        Testing Connection...
                                    </span>
                                ) : (
                                    <span className="flex items-center justify-center gap-2">
                                        <Database weight="bold" size={20} />
                                        Test Connection
                                    </span>
                                )}
                            </motion.button>

                            {/* Connection Result */}
                            {connectionResult && (
                                <div className={`p-4 rounded-xl mb-4 ${
                                    connectionResult.success 
                                        ? 'bg-green-100 border-2 border-green-500' 
                                        : 'bg-red-100 border-2 border-red-500'
                                }`}>
                                    <div className="flex items-center gap-2 mb-2">
                                        {connectionResult.success ? (
                                            <Check weight="bold" className="text-green-600" size={20} />
                                        ) : (
                                            <X weight="bold" className="text-red-600" size={20} />
                                        )}
                                        <span className={`font-bold ${connectionResult.success ? 'text-green-700' : 'text-red-700'}`}>
                                            {connectionResult.message}
                                        </span>
                                    </div>
                                    {connectionResult.details && (
                                        <div className="text-sm text-[#475569] space-y-1">
                                            <p>• Type: {connectionResult.details.is_cosmos_db ? 'Azure Cosmos DB' : 'MongoDB'}</p>
                                            <p>• Server Version: {connectionResult.details.server_version}</p>
                                            <p>• Existing Collections: {connectionResult.details.collections_count}</p>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Step 2: Deploy Schema */}
                            {connectionResult?.success && (
                                <>
                                    <div className="border-t-2 border-[#1e293b] pt-4 mt-4 mb-4">
                                        <label className="block font-fredoka font-semibold text-[#1e293b] mb-2">
                                            Step 2: Deploy Schema
                                        </label>
                                        <p className="text-sm text-[#475569] mb-3">
                                            Creates all required collections (users, missions, animals, etc.) and indexes.
                                        </p>
                                    </div>

                                    <motion.button
                                        onClick={deploySchema}
                                        disabled={deployingSchema}
                                        whileHover={{ y: -2, x: -2 }}
                                        whileTap={{ y: 2, x: 2 }}
                                        className="brutalist-btn bg-[#8b5cf6] text-white w-full mb-4 disabled:opacity-50"
                                        data-testid="deploy-schema-btn"
                                    >
                                        {deployingSchema ? (
                                            <span className="flex items-center justify-center gap-2">
                                                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                                Deploying Schema...
                                            </span>
                                        ) : (
                                            <span className="flex items-center justify-center gap-2">
                                                <Gear weight="bold" size={20} />
                                                Deploy Schema
                                            </span>
                                        )}
                                    </motion.button>

                                    {/* Schema Result */}
                                    {schemaResult && (
                                        <div className={`p-4 rounded-xl mb-4 ${
                                            schemaResult.success 
                                                ? 'bg-purple-100 border-2 border-purple-500' 
                                                : 'bg-red-100 border-2 border-red-500'
                                        }`}>
                                            <div className="flex items-center gap-2 mb-2">
                                                {schemaResult.success ? (
                                                    <Check weight="bold" className="text-purple-600" size={20} />
                                                ) : (
                                                    <X weight="bold" className="text-red-600" size={20} />
                                                )}
                                                <span className={`font-bold ${schemaResult.success ? 'text-purple-700' : 'text-red-700'}`}>
                                                    {schemaResult.message}
                                                </span>
                                            </div>
                                            {schemaResult.details && (
                                                <div className="text-sm text-[#475569] space-y-1">
                                                    <p>• Collections Created: {schemaResult.details.collections_created?.length || 0}</p>
                                                    <p>• Indexes Created: {schemaResult.details.indexes_created?.length || 0}</p>
                                                    <p>• Total Collections: {schemaResult.details.total_collections}</p>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </>
                            )}

                            {/* Step 3: Seed Data */}
                            {schemaResult?.success && (
                                <>
                                    <div className="border-t-2 border-[#1e293b] pt-4 mt-4 mb-4">
                                        <label className="block font-fredoka font-semibold text-[#1e293b] mb-2">
                                            Step 3: Seed Initial Data
                                        </label>
                                        <p className="text-sm text-[#475569] mb-3">
                                            Creates admin account, sample missions, Australian animals, badges, articles, and quizzes.
                                        </p>
                                    </div>

                                    <motion.button
                                        onClick={seedRemoteData}
                                        disabled={seedingData}
                                        whileHover={{ y: -2, x: -2 }}
                                        whileTap={{ y: 2, x: 2 }}
                                        className="brutalist-btn bg-[#22c55e] text-white w-full mb-4 disabled:opacity-50"
                                        data-testid="seed-data-btn"
                                    >
                                        {seedingData ? (
                                            <span className="flex items-center justify-center gap-2">
                                                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                                Seeding Data...
                                            </span>
                                        ) : (
                                            <span className="flex items-center justify-center gap-2">
                                                <Leaf weight="bold" size={20} />
                                                Seed Initial Data
                                            </span>
                                        )}
                                    </motion.button>

                                    {/* Seed Result */}
                                    {seedResult && (
                                        <div className={`p-4 rounded-xl ${
                                            seedResult.success 
                                                ? 'bg-green-100 border-2 border-green-500' 
                                                : 'bg-red-100 border-2 border-red-500'
                                        }`}>
                                            <div className="flex items-center gap-2 mb-2">
                                                {seedResult.success ? (
                                                    <Check weight="bold" className="text-green-600" size={20} />
                                                ) : (
                                                    <X weight="bold" className="text-red-600" size={20} />
                                                )}
                                                <span className={`font-bold ${seedResult.success ? 'text-green-700' : 'text-red-700'}`}>
                                                    {seedResult.message}
                                                </span>
                                            </div>
                                            {seedResult.details && (
                                                <div className="text-sm text-[#475569] space-y-1">
                                                    <p>• Admin Created: {seedResult.details.admin_created ? 'Yes' : 'Already exists'}</p>
                                                    <p>• Missions: {seedResult.details.missions_created}</p>
                                                    <p>• Animals: {seedResult.details.animals_created}</p>
                                                    <p>• Badges: {seedResult.details.badges_created}</p>
                                                    <p>• Articles: {seedResult.details.articles_created}</p>
                                                    <p>• Quizzes: {seedResult.details.quizzes_created}</p>
                                                </div>
                                            )}
                                            {seedResult.admin_credentials && (
                                                <div className="mt-3 p-3 bg-white rounded-lg border-2 border-green-300">
                                                    <p className="font-bold text-green-700 mb-1">Admin Credentials:</p>
                                                    <p className="font-mono text-sm">Email: {seedResult.admin_credentials.email}</p>
                                                    <p className="font-mono text-sm">Password: {seedResult.admin_credentials.password}</p>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </>
                            )}
                        </div>

                        {/* Azure Deployment Checklist */}
                        <div className="brutalist-card p-6 mb-6">
                            <h2 className="font-fredoka text-xl font-bold text-[#1e293b] mb-4 flex items-center gap-2">
                                <ShieldCheck weight="duotone" className="text-[#22c55e]" size={24} />
                                Azure Deployment Checklist
                            </h2>
                            
                            <div className="space-y-4">
                                <div className="p-4 bg-blue-50 rounded-xl border-2 border-blue-200">
                                    <h3 className="font-fredoka font-bold text-[#1e293b] mb-2 flex items-center gap-2">
                                        <Database weight="bold" className="text-blue-600" size={20} />
                                        1. Azure Cosmos DB (MongoDB API)
                                    </h3>
                                    <p className="text-sm text-[#475569] mb-3">
                                        Create a Cosmos DB account with MongoDB API. The connection string format is:
                                    </p>
                                    <div className="bg-gray-900 text-green-400 p-3 rounded-lg font-mono text-xs overflow-x-auto">
                                        mongodb://&lt;username&gt;:&lt;password&gt;@&lt;account&gt;.mongo.cosmos.azure.com:10255/?ssl=true&replicaSet=globaldb&retryWrites=false
                                    </div>
                                </div>

                                <div className="p-4 bg-purple-50 rounded-xl border-2 border-purple-200">
                                    <h3 className="font-fredoka font-bold text-[#1e293b] mb-2 flex items-center gap-2">
                                        <Key weight="bold" className="text-purple-600" size={20} />
                                        2. Environment Variables
                                    </h3>
                                    <p className="text-sm text-[#475569] mb-3">
                                        Set these in Azure App Service → Configuration → Application Settings:
                                    </p>
                                    <div className="space-y-2 font-mono text-sm">
                                        <div className="flex items-center justify-between bg-white p-2 rounded border">
                                            <span>MONGO_URL</span>
                                            <span className="text-[#475569]">Your Cosmos DB connection string</span>
                                        </div>
                                        <div className="flex items-center justify-between bg-white p-2 rounded border">
                                            <span>DB_NAME</span>
                                            <span className="text-[#475569]">bush_rangers</span>
                                        </div>
                                        <div className="flex items-center justify-between bg-white p-2 rounded border">
                                            <span>JWT_SECRET</span>
                                            <span className="text-[#475569]">Strong random string (32+ chars)</span>
                                        </div>
                                        <div className="flex items-center justify-between bg-white p-2 rounded border">
                                            <span>EMERGENT_LLM_KEY</span>
                                            <span className="text-[#475569]">Your Emergent API key</span>
                                        </div>
                                        <div className="flex items-center justify-between bg-white p-2 rounded border">
                                            <span>CORS_ORIGINS</span>
                                            <span className="text-[#475569]">https://your-app.azurewebsites.net</span>
                                        </div>
                                    </div>
                                </div>

                                <div className="p-4 bg-green-50 rounded-xl border-2 border-green-200">
                                    <h3 className="font-fredoka font-bold text-[#1e293b] mb-2 flex items-center gap-2">
                                        <Globe weight="bold" className="text-green-600" size={20} />
                                        3. App Service Configuration
                                    </h3>
                                    <ul className="text-sm text-[#475569] space-y-2">
                                        <li className="flex items-start gap-2">
                                            <Check weight="bold" className="text-green-600 mt-0.5" size={16} />
                                            <span>Set startup command: <code className="bg-gray-200 px-1 rounded">gunicorn -w 4 -k uvicorn.workers.UvicornWorker server:app</code></span>
                                        </li>
                                        <li className="flex items-start gap-2">
                                            <Check weight="bold" className="text-green-600 mt-0.5" size={16} />
                                            <span>Enable HTTPS Only in TLS/SSL settings</span>
                                        </li>
                                        <li className="flex items-start gap-2">
                                            <Check weight="bold" className="text-green-600 mt-0.5" size={16} />
                                            <span>Set health check path to: <code className="bg-gray-200 px-1 rounded">/api/health</code></span>
                                        </li>
                                        <li className="flex items-start gap-2">
                                            <Check weight="bold" className="text-green-600 mt-0.5" size={16} />
                                            <span>Configure custom domain (optional)</span>
                                        </li>
                                    </ul>
                                </div>

                                <div className="p-4 bg-yellow-50 rounded-xl border-2 border-yellow-200">
                                    <h3 className="font-fredoka font-bold text-[#1e293b] mb-2 flex items-center gap-2">
                                        <Info weight="bold" className="text-yellow-600" size={20} />
                                        4. Important Notes
                                    </h3>
                                    <ul className="text-sm text-[#475569] space-y-2">
                                        <li>• Cosmos DB requires <code className="bg-gray-200 px-1 rounded">retryWrites=false</code> in connection string</li>
                                        <li>• SSL/TLS is required for Cosmos DB connections</li>
                                        <li>• The app auto-detects Cosmos DB and adjusts settings</li>
                                        <li>• Run <code className="bg-gray-200 px-1 rounded">/api/seed</code> after deployment to populate initial data</li>
                                        <li>• Default admin: admin@bushrangers.com.au / admin123</li>
                                    </ul>
                                </div>
                            </div>
                        </div>

                        {/* Quick Links */}
                        <div className="brutalist-card p-6">
                            <h2 className="font-fredoka text-xl font-bold text-[#1e293b] mb-4">
                                Azure Portal Quick Links
                            </h2>
                            <div className="grid md:grid-cols-2 gap-3">
                                <a 
                                    href="https://portal.azure.com/#create/Microsoft.DocumentDB" 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl hover:bg-blue-50 transition-colors"
                                >
                                    <Database weight="duotone" className="text-[#38bdf8]" size={24} />
                                    <div>
                                        <p className="font-semibold text-[#1e293b]">Create Cosmos DB</p>
                                        <p className="text-xs text-[#475569]">MongoDB API database</p>
                                    </div>
                                    <ArrowSquareOut weight="bold" className="ml-auto text-[#475569]" size={20} />
                                </a>
                                <a 
                                    href="https://portal.azure.com/#create/Microsoft.WebSite" 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl hover:bg-blue-50 transition-colors"
                                >
                                    <Cloud weight="duotone" className="text-[#22c55e]" size={24} />
                                    <div>
                                        <p className="font-semibold text-[#1e293b]">Create App Service</p>
                                        <p className="text-xs text-[#475569]">Web app hosting</p>
                                    </div>
                                    <ArrowSquareOut weight="bold" className="ml-auto text-[#475569]" size={20} />
                                </a>
                            </div>
                        </div>
                    </motion.div>
                )}
                {activeTab === 'activation' && (
                    <QRActivationTool />
                )}
                {activeTab === 'emails' && (
                    <EmailPreviewTool />
                )}
            </main>

            {/* Mission Form Modal */}
            {showMissionForm && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 overflow-y-auto">
                    <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="brutalist-card p-6 w-full max-w-2xl bg-white my-8"
                    >
                        <h2 className="font-fredoka text-2xl font-bold text-[#1e293b] mb-6">
                            Create New Mission
                        </h2>
                        
                        <form onSubmit={handleCreateMission} className="space-y-4">
                            <div className="grid md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block font-fredoka font-semibold text-[#1e293b] mb-2">
                                        Title
                                    </label>
                                    <input
                                        type="text"
                                        value={missionForm.title}
                                        onChange={(e) => setMissionForm({ ...missionForm, title: e.target.value })}
                                        className="brutalist-input w-full"
                                        placeholder="Mission title"
                                        required
                                        data-testid="mission-title-input"
                                    />
                                </div>
                                <div>
                                    <label className="block font-fredoka font-semibold text-[#1e293b] mb-2">
                                        Category
                                    </label>
                                    <select
                                        value={missionForm.category}
                                        onChange={(e) => setMissionForm({ ...missionForm, category: e.target.value })}
                                        className="brutalist-input w-full"
                                        data-testid="mission-category-select"
                                    >
                                        <option value="cleanup">Cleanup</option>
                                        <option value="planting">Planting</option>
                                        <option value="water">Water Conservation</option>
                                        <option value="wildlife">Wildlife</option>
                                        <option value="education">Education</option>
                                        <option value="safety">Safety</option>
                                    </select>
                                </div>
                            </div>

                            <div>
                                <label className="block font-fredoka font-semibold text-[#1e293b] mb-2">
                                    Description
                                </label>
                                <textarea
                                    value={missionForm.description}
                                    onChange={(e) => setMissionForm({ ...missionForm, description: e.target.value })}
                                    className="brutalist-input w-full h-24 py-3"
                                    placeholder="Mission description"
                                    required
                                    data-testid="mission-description-input"
                                />
                            </div>

                            <div className="grid md:grid-cols-3 gap-4">
                                <div>
                                    <label className="block font-fredoka font-semibold text-[#1e293b] mb-2">
                                        Difficulty
                                    </label>
                                    <select
                                        value={missionForm.difficulty}
                                        onChange={(e) => setMissionForm({ ...missionForm, difficulty: e.target.value })}
                                        className="brutalist-input w-full"
                                    >
                                        <option value="easy">Easy</option>
                                        <option value="medium">Medium</option>
                                        <option value="hard">Hard</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block font-fredoka font-semibold text-[#1e293b] mb-2">
                                        Token Reward
                                    </label>
                                    <input
                                        type="number"
                                        value={missionForm.xp_reward}
                                        onChange={(e) => setMissionForm({ ...missionForm, xp_reward: parseInt(e.target.value) })}
                                        className="brutalist-input w-full"
                                        min="10"
                                        max="500"
                                    />
                                </div>
                                <div>
                                    <label className="block font-fredoka font-semibold text-[#1e293b] mb-2">
                                        Ecosystem
                                    </label>
                                    <select
                                        value={missionForm.ecosystem}
                                        onChange={(e) => setMissionForm({ ...missionForm, ecosystem: e.target.value })}
                                        className="brutalist-input w-full"
                                    >
                                        <option value="bush">Bush</option>
                                        <option value="reef">Reef</option>
                                        <option value="outback">Outback</option>
                                    </select>
                                </div>
                            </div>

                            <div>
                                <label className="block font-fredoka font-semibold text-[#1e293b] mb-2">
                                    Instructions (one per line)
                                </label>
                                <textarea
                                    value={missionForm.instructions.join('\n')}
                                    onChange={(e) => setMissionForm({ ...missionForm, instructions: e.target.value.split('\n') })}
                                    className="brutalist-input w-full h-24 py-3"
                                    placeholder="Step 1&#10;Step 2&#10;Step 3"
                                />
                            </div>

                            <div>
                                <label className="block font-fredoka font-semibold text-[#1e293b] mb-2">
                                    Image URL (optional)
                                </label>
                                <input
                                    type="url"
                                    value={missionForm.image_url}
                                    onChange={(e) => setMissionForm({ ...missionForm, image_url: e.target.value })}
                                    className="brutalist-input w-full"
                                    placeholder="https://..."
                                />
                            </div>

                            <div className="flex gap-3 pt-4">
                                <motion.button
                                    type="button"
                                    onClick={() => setShowMissionForm(false)}
                                    whileHover={{ y: -2, x: -2 }}
                                    whileTap={{ y: 2, x: 2 }}
                                    className="brutalist-btn flex-1 bg-white text-[#1e293b]"
                                >
                                    Cancel
                                </motion.button>
                                <motion.button
                                    type="submit"
                                    whileHover={{ y: -2, x: -2 }}
                                    whileTap={{ y: 2, x: 2 }}
                                    className="brutalist-btn flex-1 bg-[#22c55e] text-white"
                                    data-testid="submit-mission-btn"
                                >
                                    Create Mission
                                </motion.button>
                            </div>
                        </form>
                    </motion.div>
                </div>
            )}

            {/* AI Quiz Generator Modal */}
            {showQuizGenerator && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 overflow-y-auto">
                    <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="brutalist-card p-6 w-full max-w-2xl bg-white my-8"
                    >
                        <div className="flex justify-between items-center mb-6">
                            <h2 className="font-fredoka text-2xl font-bold text-[#1e293b] flex items-center gap-2">
                                <Sparkle weight="fill" className="text-[#8b5cf6]" />
                                AI Quiz Generator
                            </h2>
                            <button 
                                onClick={() => {
                                    setShowQuizGenerator(false);
                                    setGeneratedQuiz(null);
                                    setQuizTopic('');
                                }}
                                className="p-2 hover:bg-gray-100 rounded-full"
                                data-testid="close-quiz-generator"
                            >
                                <X weight="bold" size={24} />
                            </button>
                        </div>

                        {!generatedQuiz ? (
                            <>
                                <p className="text-[#475569] mb-4">
                                    Enter a topic to generate an AI-powered quiz for children. Topics can be about Australian wildlife, environment, conservation, or any educational subject.
                                </p>

                                <div className="mb-6">
                                    <label className="block font-fredoka font-semibold text-[#1e293b] mb-2">
                                        Quiz Topic
                                    </label>
                                    <input
                                        type="text"
                                        value={quizTopic}
                                        onChange={(e) => setQuizTopic(e.target.value)}
                                        placeholder="e.g., Great Barrier Reef, Koalas, Recycling..."
                                        className="brutalist-input w-full"
                                        data-testid="quiz-topic-input"
                                    />
                                </div>

                                <div className="mb-6">
                                    <p className="text-sm text-[#475569] mb-2">Quick topic suggestions:</p>
                                    <div className="flex flex-wrap gap-2">
                                        {['Australian Animals', 'Great Barrier Reef', 'Bushfire Safety', 'Water Conservation', 'Recycling', 'Native Plants'].map((topic) => (
                                            <button
                                                key={topic}
                                                onClick={() => setQuizTopic(topic)}
                                                className="px-3 py-1 bg-[#f1f5f9] rounded-full text-sm font-semibold text-[#475569] hover:bg-[#8b5cf6]/20 hover:text-[#8b5cf6] transition-colors"
                                            >
                                                {topic}
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                <motion.button
                                    onClick={handleGenerateQuiz}
                                    disabled={generatingQuiz || !quizTopic.trim()}
                                    whileHover={{ y: -2, x: -2 }}
                                    whileTap={{ y: 2, x: 2 }}
                                    className="brutalist-btn w-full bg-gradient-to-r from-[#8b5cf6] to-[#6366f1] text-white disabled:opacity-50"
                                    data-testid="generate-ai-quiz-btn"
                                >
                                    {generatingQuiz ? (
                                        <span className="flex items-center justify-center gap-2">
                                            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                            Generating Quiz...
                                        </span>
                                    ) : (
                                        <span className="flex items-center justify-center gap-2">
                                            <Lightning weight="fill" size={20} />
                                            Generate Quiz
                                        </span>
                                    )}
                                </motion.button>
                            </>
                        ) : (
                            <>
                                <div className="mb-6 p-4 bg-[#22c55e]/10 rounded-xl border-2 border-[#22c55e]">
                                    <div className="flex items-center gap-2 mb-2">
                                        <Check weight="bold" className="text-[#22c55e]" size={20} />
                                        <span className="font-fredoka font-bold text-[#22c55e]">Quiz Generated!</span>
                                    </div>
                                    <p className="text-sm text-[#475569]">Review the quiz below and save it to your content library.</p>
                                </div>

                                <div className="mb-6">
                                    <h3 className="font-fredoka text-xl font-bold text-[#1e293b] mb-4">
                                        {generatedQuiz.title}
                                    </h3>
                                    
                                    <div className="space-y-4 max-h-64 overflow-y-auto">
                                        {generatedQuiz.questions?.map((q, index) => (
                                            <div key={index} className="p-4 bg-[#f1f5f9] rounded-xl">
                                                <p className="font-semibold text-[#1e293b] mb-2">
                                                    {index + 1}. {q.question}
                                                </p>
                                                <div className="grid grid-cols-2 gap-2">
                                                    {q.options?.map((option, oIndex) => (
                                                        <div 
                                                            key={oIndex}
                                                            className={`p-2 rounded-lg text-sm ${
                                                                oIndex === q.correct 
                                                                    ? 'bg-[#22c55e]/20 text-[#22c55e] font-semibold' 
                                                                    : 'bg-white text-[#475569]'
                                                            }`}
                                                        >
                                                            {option}
                                                            {oIndex === q.correct && ' ✓'}
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                <div className="flex gap-3">
                                    <motion.button
                                        onClick={() => {
                                            setGeneratedQuiz(null);
                                            setQuizTopic('');
                                        }}
                                        whileHover={{ y: -2, x: -2 }}
                                        whileTap={{ y: 2, x: 2 }}
                                        className="brutalist-btn flex-1 bg-white text-[#1e293b]"
                                    >
                                        Generate Another
                                    </motion.button>
                                    <motion.button
                                        onClick={handleSaveGeneratedQuiz}
                                        whileHover={{ y: -2, x: -2 }}
                                        whileTap={{ y: 2, x: 2 }}
                                        className="brutalist-btn flex-1 bg-[#22c55e] text-white"
                                        data-testid="save-generated-quiz-btn"
                                    >
                                        <span className="flex items-center justify-center gap-2">
                                            <Check weight="bold" size={20} />
                                            Save Quiz
                                        </span>
                                    </motion.button>
                                </div>
                            </>
                        )}
                    </motion.div>
                </div>
            )}
        </div>
    );
}
